import * as React from 'react';

import { Slider as UiSlider } from 'components/ui/slider';
import { Input } from 'components/ui/input';
import { cn } from 'lib/utils';

type Size = 'sm' | 'md' | 'lg';

type SliderProps = {
  defaultValue: number;
  step?: number | string;
  size?: Size;
  minValue?: number;
  maxValue?: number;
  label?: string;
  unit?: string;
  onChange: (slider: number) => void;
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const DEBOUNCE_DELAY = 75;

const sizeConfig: Record<
  Size,
  { track: string; thumb: string; input: string; trackContainer: string }
> = {
  sm: {
    track: 'h-1',
    thumb: 'h-4 w-4',
    input: 'h-8 px-2 text-xs',
    trackContainer: 'gap-1',
  },
  md: {
    track: 'h-2',
    thumb: 'h-5 w-5',
    input: 'h-10',
    trackContainer: 'gap-2',
  },
  lg: {
    track: 'h-3',
    thumb: 'h-6 w-6',
    input: 'h-12 px-4 text-base',
    trackContainer: 'gap-3',
  },
};

export const Slider: React.FC<SliderProps> = ({
  defaultValue,
  label,
  onChange,
  size = 'lg',
  minValue = 0,
  maxValue = Infinity,
  step = 1,
  unit = '',
}) => {
  const parsedStep =
    typeof step === 'number' ? step : parseFloat(step.toString()) || 1;
  const resolvedMax = Number.isFinite(maxValue) ? maxValue : 100;
  const changeTimeoutRef = React.useRef<number | undefined>(undefined);
  const initialValue = React.useMemo(
    () => clamp(defaultValue, minValue, resolvedMax),
    [defaultValue, minValue, resolvedMax]
  );
  const [value, setValue] = React.useState<number>(initialValue);
  const [inputValue, setInputValue] = React.useState<string>(
    initialValue.toString()
  );

  const clearPendingChange = React.useCallback(() => {
    if (
      changeTimeoutRef.current !== undefined &&
      typeof window !== 'undefined'
    ) {
      window.clearTimeout(changeTimeoutRef.current);
    }
    changeTimeoutRef.current = undefined;
  }, []);

  const updateValueState = React.useCallback((nextValue: number) => {
    setValue(nextValue);
    setInputValue(nextValue.toString());
  }, []);

  const emitChange = React.useCallback(
    (nextValue: number, { debounce }: { debounce: boolean }) => {
      if (!debounce || typeof window === 'undefined') {
        clearPendingChange();
        onChange(nextValue);
        return;
      }

      clearPendingChange();
      changeTimeoutRef.current = window.setTimeout(() => {
        onChange(nextValue);
        changeTimeoutRef.current = undefined;
      }, DEBOUNCE_DELAY);
    },
    [clearPendingChange, onChange]
  );

  React.useEffect(() => {
    const nextValue = clamp(defaultValue, minValue, resolvedMax);
    updateValueState(nextValue);
    clearPendingChange();
  }, [
    clearPendingChange,
    defaultValue,
    minValue,
    resolvedMax,
    updateValueState,
  ]);

  React.useEffect(() => {
    return () => {
      clearPendingChange();
    };
  }, [clearPendingChange]);

  const commitValue = React.useCallback(
    (nextValue: number, { debounce = false }: { debounce?: boolean } = {}) => {
      const clamped = clamp(nextValue, minValue, resolvedMax);
      updateValueState(clamped);
      emitChange(clamped, { debounce });
    },
    [emitChange, minValue, resolvedMax, updateValueState]
  );

  const handleSliderChange = React.useCallback(
    (sliderValues: number[]) => {
      const [nextValue = value] = sliderValues;
      commitValue(nextValue, { debounce: true });
    },
    [commitValue, value]
  );

  const handleSliderCommit = React.useCallback(
    (sliderValues: number[]) => {
      const [nextValue = value] = sliderValues;
      commitValue(nextValue);
    },
    [commitValue, value]
  );

  const handleTextChange: React.ChangeEventHandler<HTMLInputElement> =
    React.useCallback((event) => {
      setInputValue(event.target.value);
    }, []);

  const handleTextBlur: React.FocusEventHandler<HTMLInputElement> =
    React.useCallback(
      (event) => {
        const parsed = parseFloat(event.target.value);
        commitValue(!Number.isNaN(parsed) ? parsed : value);
      },
      [commitValue, value]
    );

  const {
    track,
    thumb,
    input: inputClasses,
    trackContainer,
  } = sizeConfig[size];

  return (
    <div className="flex flex-row items-start">
      <div className={cn('flex w-full flex-grow flex-col', trackContainer)}>
        {label ? (
          <label className="block text-sm font-medium text-gray-900">
            {label}
          </label>
        ) : null}
        <UiSlider
          value={[value]}
          min={minValue}
          max={resolvedMax}
          step={parsedStep}
          onValueChange={handleSliderChange}
          onValueCommit={handleSliderCommit}
          trackClassName={track}
          thumbClassName={thumb}
          data-testid="slider"
          data-value={value}
        />
        <ul className="flex justify-between text-xs text-gray-600">
          <li className="flex justify-center">{`${minValue.toFixed(
            0
          )}${unit}`}</li>
          <li className="flex justify-center">{`${resolvedMax.toFixed(
            0
          )}${unit}`}</li>
        </ul>
      </div>
      <div className="ml-3 w-2/12">
        <Input
          className={cn('w-full', inputClasses)}
          value={inputValue}
          onChange={handleTextChange}
          onBlur={handleTextBlur}
          data-testid="slider-text-input"
        />
      </div>
    </div>
  );
};
